import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, setCsrfToken } from "./api";

export interface MfaState {
  required: boolean;
  satisfied: boolean;
  totp: "not_required" | "setup_required" | "verify_required" | "verified";
  passkey: "not_required" | "setup_required" | "verify_required" | "verified";
  googleRequired: boolean;
  googleSatisfied: boolean;
}

export interface Me {
  id: string;
  fullName: string;
  email: string | null;
  emailVerified: boolean;
  phone: string | null;
  phoneVerified: boolean;
  role: string;
  roleLabel: string;
  accountStatus: string;
  isSynthetic: boolean;
  workspace: string;
  preferredLanguage: "en" | "hi";
  emailNotifications: boolean;
  notificationPrefs: { email?: boolean; area_activity?: boolean };
  civicDesignations: string[];
  restrictedUntil: string | null;
  affiliation: { organisationId: string; public: boolean } | null;
  geography: {
    pinCode: string | null;
    locality: string | null;
    state: string | null;
    district: string | null;
    assemblyConstituency: string | null;
    parliamentaryConstituency: string | null;
    localBody: string | null;
    ward: string | null;
    geographyId: string | null;
  };
  linkedIdentities: { id: string; provider: string; identifier: string | null; linkedAt: string; lastUsedAt: string | null }[];
  security: { totpEnrolled: boolean; passkeys: { id: string; label: string | null; createdAt: string; lastUsedAt: string | null }[]; authMethod: string };
  unreadNotifications: number;
}

export interface Access {
  permissions: string[];
  groups: { key: string; label: string }[];
  modules: { key: string; label: string }[];
  geography: { global: boolean; states: string[]; districts: string[]; assemblyConstituencies: string[]; parliamentaryConstituencies: string[]; localBodies: string[] };
  representative: any;
  delegations: any[];
  parties: { id: string; name: string; abbreviation: string | null }[];
  organisations: { id: string; name: string; org_type: string }[];
}

export interface PublicConfig {
  environment: "production" | "demo" | "development" | "test";
  publicOrigin: string;
  auth: { configured: boolean; google: boolean; email: boolean; phone: boolean; demo: boolean };
  turnstileSiteKey: string | null;
  limits: { maxFiles: number; maxFileMb: number; dailySubmissions: number };
}

interface SessionValue {
  loading: boolean;
  config: PublicConfig | null;
  configError: string | null;
  user: Me | null;
  access: Access | null;
  mfa: MfaState | null;
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [user, setUser] = useState<Me | null>(null);
  const [access, setAccess] = useState<Access | null>(null);
  const [mfa, setMfa] = useState<MfaState | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get("/api/me");
      setCsrfToken(me.csrfToken ?? null);
      setUser(me.user ?? null);
      setAccess(me.access ?? null);
      setMfa(me.mfa ?? null);
    } catch {
      setUser(null);
      setAccess(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setConfig(await api.get("/api/config"));
      } catch (e) {
        setConfigError((e as Error).message);
      }
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const value = useMemo<SessionValue>(
    () => ({
      loading,
      config,
      configError,
      user,
      access,
      mfa,
      refresh,
      can: (perm) => Boolean(access?.permissions.includes(perm)),
      signOut: async () => {
        const r = await api.post("/auth/logout");
        setUser(null);
        setAccess(null);
        setCsrfToken(null);
        window.location.href = r?.redirect || "/";
      },
    }),
    [loading, config, configError, user, access, mfa, refresh],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside provider");
  return v;
}

export function signInUrl(method?: string, returnTo?: string): string {
  const p = new URLSearchParams();
  if (method) p.set("method", method);
  if (returnTo) p.set("returnTo", returnTo);
  const s = p.toString();
  return `/auth/login${s ? `?${s}` : ""}`;
}

export function workspaceHome(workspace: string | undefined): string {
  return !workspace || workspace === "citizen" ? "/account" : "/workspace";
}
